import React, { useEffect, useRef, useState } from 'react';
import { apiFetch } from '../lib/api';

// @@COMPANY_SIGS_v127@@ Admin-only generic signatures for shared company mailboxes (info@, orders@, …).
// Rendered as the "Company" tab on the Signature page. Same renderer + click tracking as personal signatures;
// the server gates every call to role admin.

const BLANK = { label: '', name: '', title: '', company: 'XYZ Merchandise', phone: '', email: '', website: 'xyzmerch.com' };

function fmt(s) { if (!s) return 'never'; try { return new Date(s).toLocaleDateString(); } catch { return '—'; } }

export default function CompanySignatures() {
  const [list, setList] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [editing, setEditing] = useState(null); // null | 'new' | <id>
  const [form, setForm] = useState(BLANK);
  const [html, setHtml] = useState('');
  const [stats, setStats] = useState([]);
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const [copiedSig, setCopiedSig] = useState(false);
  const previewRef = useRef(null);

  async function loadList() {
    const res = await apiFetch('/api/signature/company');
    const d = await res.json().catch(() => ({}));
    if (res.ok) { setList(d.signatures || []); setMsg(''); }
    else setMsg(d.error || 'Could not load company signatures.');
    setLoaded(true);
  }
  useEffect(() => { loadList(); }, []);

  function applyPayload(d) {
    const s = d.signature || {};
    setForm({
      label: s.label || '', name: s.name || '', title: s.title || '', company: s.company || '',
      phone: s.phone || '', email: s.email || '', website: s.website || '',
    });
    setHtml(d.html || '');
    setStats(d.stats || []);
  }

  function startNew() {
    setEditing('new'); setForm(BLANK); setHtml(''); setStats([]); setMsg('');
  }

  async function open(id) {
    setMsg('');
    const res = await apiFetch('/api/signature/company/' + id);
    const d = await res.json().catch(() => ({}));
    if (!res.ok) { setMsg(d.error || 'Could not open that signature.'); return; }
    setEditing(id); applyPayload(d);
  }

  function close() {
    setEditing(null); setHtml(''); setStats([]); setMsg(''); loadList();
  }

  async function save() {
    setMsg('');
    if (!form.email.trim()) { setMsg('Email is required.'); return; }
    setBusy(true);
    const isNew = editing === 'new';
    const res = await apiFetch('/api/signature/company' + (isNew ? '' : '/' + editing), {
      method: isNew ? 'POST' : 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(form),
    });
    const d = await res.json().catch(() => ({}));
    setBusy(false);
    if (!res.ok) { setMsg(d.error || 'Could not save.'); return; }
    applyPayload(d);
    if (isNew && d.signature) setEditing(d.signature.id);
    setMsg('Saved. Re-copy and re-install the signature if it was already in use — saving issues new tracked links.');
  }

  async function remove() {
    if (editing === 'new' || !editing) return;
    if (!window.confirm('Delete this company signature? Copies already installed in Mail will stop tracking clicks.')) return;
    setBusy(true);
    const res = await apiFetch('/api/signature/company/' + editing, { method: 'DELETE' });
    setBusy(false);
    if (res.ok) close();
    else { const d = await res.json().catch(() => ({})); setMsg(d.error || 'Could not delete.'); }
  }

  function copyHtml() {
    if (!html) return;
    navigator.clipboard.writeText(html).then(() => {
      setCopied(true); setTimeout(() => setCopied(false), 2000);
    }).catch(() => {});
  }

  async function copyRendered() {
    const node = previewRef.current;
    if (!node) return;
    const markup = node.innerHTML;
    try {
      if (navigator.clipboard && window.ClipboardItem) {
        const item = new window.ClipboardItem({
          'text/html': new Blob([markup], { type: 'text/html' }),
          'text/plain': new Blob([node.innerText || ''], { type: 'text/plain' }),
        });
        await navigator.clipboard.write([item]);
      } else {
        const range = document.createRange();
        range.selectNodeContents(node);
        const sel = window.getSelection();
        sel.removeAllRanges(); sel.addRange(range);
        document.execCommand('copy');
        sel.removeAllRanges();
      }
      setCopiedSig(true); setTimeout(() => setCopiedSig(false), 2000);
    } catch (e) { /* clipboard blocked; user can select manually */ }
  }

  const field = (k, label, type = 'text') => (
    <input className="hq-input" type={type} placeholder={label} value={form[k]}
      onChange={(e) => setForm({ ...form, [k]: e.target.value })} style={{ minWidth: 200 }} />
  );

  if (!editing) {
    return (
      <div className="hq-panel" style={{ marginBottom: 20 }}>
        <div className="hq-panel-head">
          <h2>Company signatures</h2>
          <button className="hq-btn" onClick={startNew}>New signature</button>
        </div>
        {msg && <p className="hq-msg" style={{ padding: '0 20px' }}>{msg}</p>}
        <table className="hq-table">
          <thead><tr><th>Name</th><th>Email</th><th>Clicks</th><th>Updated</th><th></th></tr></thead>
          <tbody>
            {list.map((s) => (
              <tr key={s.id}>
                <td>{s.label || s.email}</td>
                <td>{s.email}</td>
                <td>{s.clicks}</td>
                <td>{fmt(s.updated_at)}</td>
                <td style={{ textAlign: 'right' }}>
                  <button className="hq-btn hq-btn-ghost" onClick={() => open(s.id)}>Open</button>
                </td>
              </tr>
            ))}
            {loaded && list.length === 0 && (
              <tr><td className="hq-empty" colSpan={5}>No company signatures yet. Create one for a shared mailbox like info@ or orders@.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    );
  }

  return (
    <>
      <div className="hq-panel" style={{ marginBottom: 20 }}>
        <div className="hq-panel-head">
          <h2>{editing === 'new' ? 'New company signature' : (form.label || form.email || 'Company signature')}</h2>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="hq-btn hq-btn-ghost" onClick={close} disabled={busy}>Back to list</button>
            {editing !== 'new' && <button className="hq-btn hq-btn-ghost" onClick={remove} disabled={busy}>Delete</button>}
            <button className="hq-btn" onClick={save} disabled={busy}>{busy ? 'Saving…' : 'Save'}</button>
          </div>
        </div>
        <div style={{ padding: 20 }}>
          <div className="hq-form">
            {field('label', 'Internal name (e.g. Orders inbox)')}
            {field('name', 'Display name (e.g. XYZ Orders Team)')}
            {field('title', 'Line under the name (optional)')}
            {field('company', 'Company')}
            {field('phone', 'Phone')}
            {field('email', 'Mailbox email (required)', 'email')}
            {field('website', 'Website')}
          </div>
          {msg && <p className="hq-msg">{msg}</p>}
        </div>
      </div>

      <div className="hq-panel" style={{ marginBottom: 20 }}>
        <div className="hq-panel-head">
          <h2>Preview &amp; HTML</h2>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="hq-btn" onClick={copyRendered} disabled={!html}>{copiedSig ? 'Copied!' : 'Copy signature'}</button>
            <button className="hq-btn" onClick={copyHtml} disabled={!html}>{copied ? 'Copied!' : 'Copy HTML'}</button>
          </div>
        </div>
        <div style={{ padding: 20 }}>
          {html ? (
            <>
              <div ref={previewRef} style={{ background: '#fff', borderRadius: 10, padding: 16, marginBottom: 14 }}
                dangerouslySetInnerHTML={{ __html: html }} />
              <textarea readOnly value={html} className="hq-input"
                style={{ width: '100%', height: 120, padding: 12, fontFamily: 'monospace', fontSize: 12, resize: 'vertical' }} />
              <p style={{ margin: '12px 0 0', fontSize: 13, color: 'var(--hq-muted)' }}>
                Install it the same way as a personal signature (steps on the My signature tab) — in Mail, pick the shared
                mailbox's account before adding the signature.
              </p>
            </>
          ) : (
            <p className="hq-empty" style={{ padding: 0 }}>Save the details to generate the signature.</p>
          )}
        </div>
      </div>

      {editing !== 'new' && (
        <div className="hq-panel">
          <div className="hq-panel-head"><h2>Link clicks</h2></div>
          <table className="hq-table">
            <thead><tr><th>Link</th><th>Clicks</th><th>Last click</th></tr></thead>
            <tbody>
              {stats.map((s) => (
                <tr key={s.kind}>
                  <td style={{ textTransform: 'capitalize' }}>{s.kind}</td>
                  <td>{s.clicks}</td>
                  <td>{fmt(s.last_click)}</td>
                </tr>
              ))}
              {stats.length === 0 && (
                <tr><td className="hq-empty" colSpan={3}>No tracked links yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
